/**
 * B776 (a′, the design seat's ruling of 2026-10-12 under Pathik's standing
 * instruction) — a signed-in member's price online. The buyer's signed-in
 * session (B632, its own key per tenant) reads their membership; the GUEST
 * cart is told about it through `cart.set_member`, with the signed-in token
 * in the body as the proof the server checks. The signed-in token is never
 * the cart's bearer, and nothing here can stand between a buyer and their
 * cart: a refusal or a failed read leaves the price at list.
 */
import { portalMembers, type PortalMember } from "./meMembership.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

/** The membership that prices a cart: ACTIVE before GRACE, the later term between two of a kind; none lapsed, canceled or an unredeemed gift. */
export function pickCartMember(members: readonly PortalMember[]): PortalMember | undefined {
  const rank = (m: PortalMember): number => (m.status === "ACTIVE" ? 2 : m.status === "GRACE" ? 1 : 0);
  return [...members].filter((m) => rank(m) > 0).sort((a, b) => rank(b) - rank(a) || b.term.end - a.term.end)[0];
}

export type MemberCartOutcome = "named" | "none" | "refused";

export async function nameMemberOnCart(opts: {
  readonly guest: Caller;
  readonly signedIn: Caller;
  readonly cartId: string;
  readonly meToken: string;
}): Promise<MemberCartOutcome> {
  try {
    const member = pickCartMember(portalMembers(await opts.signedIn.call("membership.list", {})));
    if (member === undefined) return "none";
    await opts.guest.call("cart.set_member", { cartId: opts.cartId, memberNumber: member.memberNumber, meSession: opts.meToken });
    return "named";
  } catch {
    return "refused";
  }
}
